<script setup lang="ts">
import PageLabel from "components/common/PageLabel.vue";
import {onMounted, ref} from "vue";
import {MyFileUploader, UploadRecord} from "@/types";
import {TimeFormatter} from "@/utils/common.ts";
import AddImageDialog from "components/dialog/common/AddImageDialog.vue";
import {uploadImage} from "@/utils/cos.ts";
import {ElNotification, genFileId} from "element-plus";

const clipboard = navigator.clipboard || {
  writeText: (text: string) => {
    let copyInput = document.createElement("input");
    copyInput.value = text;
    document.body.appendChild(copyInput);
    copyInput.select();
    document.execCommand("copy");
    document.body.removeChild(copyInput);
  }
}
const addImageUpload = ref<InstanceType<typeof AddImageDialog>>()
const uploadRecords = ref<UploadRecord[]>([])

onMounted(() => {
  const savedRecords = localStorage.getItem('uploadRecords')
  if (savedRecords) {
    try {
      uploadRecords.value = JSON.parse(savedRecords)
    } catch (err) {
      console.warn("解析数据失败")
    }
  }
})

const onUploadImage = () => {
  addImageUpload.value?.show()
}

const onStartUpload = (fileUploaders: MyFileUploader[]) => {
  if (fileUploaders && fileUploaders.length > 0) {
    const newRecords: UploadRecord[] = []
    fileUploaders.forEach(fileUploader => {
      let newRecord: UploadRecord = {
        id: genFileId(),
        fileName: fileUploader.file.name,
        uploadTime: Date.now(),
        status: 'uploading',
        progress: 0,
        url: '',
      }
      newRecords.push(newRecord)
      uploadImage(fileUploader.file, {
        onTaskReady(_: string) {
        },
        onProgress(progressData: any) {
          console.log(progressData.percent)
          const recordIndex = uploadRecords.value.findIndex(
              record => record.id === newRecord.id);
          if (recordIndex !== -1) {
            uploadRecords.value[recordIndex].progress = progressData.percent * 100;
          }
        },
        onUploadFail(err: string) {
          console.log(err)
          const recordIndex = uploadRecords.value.findIndex(
              record => record.id === newRecord.id);
          if (recordIndex !== -1) {
            uploadRecords.value[recordIndex].status = 'error';
          }
        },
        onFileFinish(url: string) {
          newRecord.status = 'success'
          const recordIndex = uploadRecords.value.findIndex(
              record => record.id === newRecord.id);
          if (recordIndex !== -1) {
            uploadRecords.value[recordIndex].status = 'success';
            uploadRecords.value[recordIndex].url = url;
            localStorage.setItem("uploadRecords", JSON.stringify(uploadRecords.value));
          }
        }
      })
    })
    uploadRecords.value.unshift(...newRecords)
    if (uploadRecords.value.length > 100) {
      uploadRecords.value.slice(0, uploadRecords.value.length - 100)
    }
    addImageUpload.value?.destroy()
  }
}

const onRemove = (_record: UploadRecord) => {
  const recordIndex = uploadRecords.value.findIndex(record => record.id === _record.id);
  if (recordIndex !== -1) {
    uploadRecords.value.splice(recordIndex, 1);
  }
  localStorage.setItem("uploadRecords", JSON.stringify(uploadRecords.value));
}

const onCopy = (url: string) => {
  clipboard.writeText(url)
  ElNotification.success({
    message: "图片地址已复制",
  })
}
</script>

<template>
  <div class="mx-3rem mt-2.25rem">
    <page-label>图片上传</page-label>
    <div class="p-1rem bg-white rounded-md mt-1rem shadow-md">
      <div class="flex gap-.5rem items-center">
        <el-button type="primary" @click="onUploadImage">上传图片</el-button>
        <div class="mr-.5rem text-gray-400">{{ `仅显示在本浏览器(不区分用户)上传的最近100条记录` }}</div>
      </div>
      <div class="mt-1rem ">
        <el-table :data="uploadRecords">
          <el-table-column label="文件名" prop="fileName">
          </el-table-column>
          <el-table-column label="图片显示" prop="url">
            <template #default="scope">
              <el-image
                  style="width: 100px; height: 100px"
                  :src="scope.row.url"
                  :zoom-rate="1.2"
                  :max-scale="7"
                  :min-scale="0.2"
                  :preview-src-list="[scope.row.url]"
                  show-progress
                  :initial-index="1"
                  :preview-teleported="true"
                  fit="cover"
              />
            </template>
          </el-table-column>
          <el-table-column label="上传时间" prop="uploadTime">
            <template #default="scope">
              {{ TimeFormatter.formatTime(scope.row.uploadTime) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" prop="status">
            <template #default="scope">
              {{
                scope.row.status === "success" ? "上传成功" :
                    scope.row.status === "error" ? "上传失败" :
                        scope.row.status === "uploading" ? scope.row.progress :
                            "上传失败"
              }}
            </template>
          </el-table-column>
          <el-table-column label="操作" prop="operate">
            <template #default="scope">
              <el-button type="primary" v-if="scope.row.url" @click="onCopy(scope.row.url)">复制</el-button>
              <el-button type="danger" @click="onRemove(scope.row)">移除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
  <add-image-dialog ref="addImageUpload" @on-start-upload="onStartUpload"/>
</template>

<style scoped>
</style>
